import { resetRequests } from "@redux-requests/core";
import { getAllActiveWorkspaces } from "@redux/memoizedSelectors";
import { encode } from "@utils/helpers";
import { UserType } from "@utils/optionsSets/OptionSets";
import axios from "axios";
import { currentWorkspaceId } from "index";
import $ from "jquery";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import { insertRecentWork } from "../../features/settings/reducer/UserActions";
import { useItem } from "shared/dynamic-imports";

export const useClickOutside = (ref, handler, authorizedClasses = [], unauthorizedClasses = []) => {
	useEffect(() => {
		const listener = (event) => {
			//console.log("event", event.target.className);
			// Check if the click is outside the ref element
			if (!ref.current) {
				//  console.log("Ref is not defined",ref);
				return;
			}

			if (ref?.current?.contains(event.target)) {
				//  console.log("Clicked inside the ref element");
				return;
			}

			if (authorizedClasses?.some((name) => typeof event.target.className === "string" && event.target.className.startsWith(name))) {
				// console.log("Clicked inside an authorized class");
				return;
			}

			handler(event);
		};
		document.addEventListener("mousedown", listener);
		document.addEventListener("touchstart", listener);
		return () => {
			document.removeEventListener("mousedown", listener);
			document.removeEventListener("touchstart", listener);
		};
	}, [ref, handler]);
};

export const useClickOutsides = (handler, authorizedClasses) => {
	const ref = useRef(null);
	useEffect(() => {
		const listener = (event) => {
			if (!ref.current || ref.current?.contains(event.target) || (typeof event.target.className === "string" && authorizedClasses?.some((name) => event.target.className?.startsWith(name)))) {
				return;
			}
			handler(event);
		};
		document.addEventListener("mousedown", listener);
		document.addEventListener("touchstart", listener);
		return () => {
			document.removeEventListener("mousedown", listener);
			document.removeEventListener("touchstart", listener);
		};
	}, [ref, handler]);
	return ref;
};

export const useDoubleClickOutside = (callback) => {
	const ref = React.useRef();

	React.useEffect(() => {
		const handleClick = (event) => {
			if (ref.current && !ref.current.contains(event.target)) {
				callback(event);
			}
		};

		document.addEventListener("click", handleClick, true);

		return () => {
			document.removeEventListener("click", handleClick, true);
		};
	}, [ref]);

	return ref;
};

export function useAsync({ asyncFn, onSuccess = null, loading = null, parameter = null, reset = null }) {
	const dispatch = useDispatch();
	useEffect(() => {
		let isActive = true;
		loading !== null && loading(true);
		asyncFn !== null &&
			dispatch(asyncFn(parameter))
				?.then((data) => {
					if (isActive) {
						onSuccess !== null && onSuccess(data);
						loading !== null && loading(false);
						//  toast.success("Successfully mounted")
					} else {
						//console.log("aborted setState on unmounted component")
					}
				})
				.catch((error) => {
					loading !== null && isActive && loading(false);
					toast.error(error.response);
				});
		return () => {
			isActive = false;
			resetRequests();
			reset !== null && dispatch(reset());
		};
	}, [asyncFn, onSuccess]);
}

export function useAsyncs({ asyncFns = [], setLoading, handleSuccess, resets = null }) {
	const dispatch = useDispatch();

	useEffect(() => {
		// Array to hold abort controllers for each async operation
		const abortControllers = asyncFns.map(() => new AbortController());
		let isMounted = true;

		if (asyncFns.length > 0) {
			setLoading && setLoading(true);

			const promises = asyncFns.map(({ asyncFn, parameter, Fn }, index) => {
				// Pass the abort signal if the function supports cancellation
				const controller = abortControllers[index];
				if (Fn !== undefined) {
					return Fn(parameter, controller.signal);
				} else if (asyncFn) {
					return dispatch(asyncFn(parameter, controller.signal));
				}
			});

			Promise.all(promises)
				.then((data) => {
					if (isMounted) {
						setLoading && setLoading(false);
						handleSuccess && handleSuccess();
					}
				})
				.catch((error) => {
					// Handle errors; if the error is due to abortion, you might choose to ignore it.
					if (isMounted) {
						setLoading && setLoading(false);
						toast.error(error.response || error.message);
					}
				});
		}

		return () => {
			// Mark component as unmounted
			isMounted = false;
			// Abort all ongoing asynchronous operations
			abortControllers.forEach((controller) => controller.abort());

			if (resets !== null) {
				const resetPromises = resets.map((fn) => fn && dispatch(fn()));
				Promise.all(resetPromises);
			}
		};
	}, []);

	return null;
}

export const useContainerDimensions = (myRef) => {
	const getDimensions = () => ({
		width: myRef.current.offsetWidth,
		height: myRef.current.offsetHeight,
	});

	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	useEffect(() => {
		const handleResize = () => {
			setDimensions(getDimensions());
		};

		if (myRef.current) {
			setDimensions(getDimensions());
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [myRef]);

	return dimensions;
};

export const useAutoFocus = () => {
	const inputRef = useRef(null);

	useEffect(() => {
		setTimeout(() => {
			inputRef.current?.focus();
		}, 1);
	}, []);

	return inputRef;
};

export const useIsEllipsis = (disableIfEllipsis) => {
	const ref = useRef();
	const [isEllipsis, setIsEllipsis] = useState(false);

	useEffect(() => {
		if (disableIfEllipsis && ref.current) {
			const { scrollWidth, clientWidth } = ref.current;
			setIsEllipsis(scrollWidth > clientWidth);
		}
	}, [disableIfEllipsis]);

	return [ref, isEllipsis];
};

export function useDebounce(value, delay) {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
}
export function useIsMounted() {
	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	return isMountedRef; // Return the ref, not just its current value
}
export const useItemState = (initialAction, updateService) => {
	const [currentItem, setCurrentItem] = useState(initialAction);
	const [modifiedAttribNames, setModifiedAttribNames] = useState(new Set());
	const current_user = useSelector((state) => state.maps.currentUser);

	const updateAttribute = (attributeId, attributeName, attributeValue) => {
		const updatedAttribute = currentItem.attributes.map((attr) => {
			if (attr.id === attributeId) {
				return { ...attr, [attributeName]: attributeValue };
			}
			return attr;
		});
		setCurrentItem((prevState) => ({
			...prevState,
			attributes: updatedAttribute,
			lastUpdated: new Date().toISOString(),
			modifiedBy: current_user?.fullName,
		}));
	};

	const handleChange = (name, value, allowDirectServiceUpdate, updateModifiedAttribute = true) => {
		setCurrentItem((prevState) => ({
			...prevState,
			[name]: value,
			lastUpdated: new Date().toISOString(),
			modifiedBy: current_user?.fullName,
		}));
		if (allowDirectServiceUpdate) return updateService({ item: { ...currentItem, [name]: value }, modifiedAttribNames: [name] });

		if (updateModifiedAttribute) setModifiedAttribNames((prevSet) => new Set(prevSet).add(name));
	};

	const handleBlur = (attribute) => {
		// Add attribute-specific logic here if needed
		// For example, encoding description and subject
		let description = currentItem.description || "";
		let name = currentItem.name || "";
		if (currentItem.description) description = encode(currentItem.description);
		if (currentItem.name) name = encode(currentItem.name);

		// Update the service with the modified attributes using the provided function
		updateService({ item: { ...currentItem, description, name }, modifiedAttribNames: [attribute] });

		// Remove the attribute from the modified set
		setModifiedAttribNames((prevSet) => {
			const updatedSet = new Set(prevSet);
			updatedSet.delete(attribute);
			return updatedSet;
		});
	};

	useEffect(() => {
		return () => {
			// Cleanup function to reset the state when the component unmounts
			// console.log("unmounting");
			setCurrentItem(null);
			setModifiedAttribNames(new Set());
		};
	}, []);

	return { currentItem, setCurrentItem, handleChange, updateAttribute, handleBlur, setModifiedAttribNames, modifiedAttribNames, updateService };
};
export const useNavigationListData = ({ allItemsSelector, selectedItemsIds = "", getSelectedItemsIds = undefined, update = undefined }) => {
	// navigation(arrow) for opportunities/ actions/ solutions based on filteredIds on the table
	// getSelectedItemsIds return the path with the entity type and context (ex: ({ ui }: any) => ui.table?.entity?.[EntityList.Opportunity]?.OpportunitiesList?.filteredIds)
	// allItemsSelector return the list of all items (ex: () => getAllOpportunities)
	// note ! this hook work with the item provider only

	const { pathname } = useLocation();
	const { setCurrentItem } = useItem();
	const allItems = useSelector(allItemsSelector) || [];
	const itemIds = useSelector(getSelectedItemsIds ? getSelectedItemsIds(pathname) : () => {});

	const getNavigationList = () => {
		if (selectedItemsIds) return selectedItemsIds;
		if (itemIds) return itemIds;
		return [];
	};
	const navigationList = getNavigationList();

	const updateCurrentItem = (id) => {
		if (update) {
			update(id);
		} else {
			const newItem = allItems?.find((item) => item.id === id);
			setCurrentItem(newItem);
		}
	};
	return { navigationList, updateCurrentItem };
};

export const useAttributeHandler = (initialAction, updateService) => {
	const [object, setObject] = useState(initialAction);
	const [modifiedAttribNames, setModifiedAttribNames] = useState(new Set());

	const handleChange = (name, value, allowDirectServiceUpdate) => {
		setObject((prevState) => ({
			...prevState,
			[name]: value,
		}));
		if (allowDirectServiceUpdate) return updateService({ ...object, [name]: value }, [name]);

		setModifiedAttribNames((prevSet) => new Set(prevSet).add(name));
	};

	const handleBlur = (attribute) => {
		if (modifiedAttribNames.has(attribute)) {
			// Add attribute-specific logic here if needed
			// For example, encoding description and subject
			let description = object.description;
			let subject = object.subject;
			let name = object.name;
			if (attribute === "description") description = encode(object.description);
			if (attribute === "subject") subject = encode(object.subject);
			if (attribute === "name") name = encode(object.name);

			// Update the service with the modified attributes using the provided function
			updateService({ ...object, description, subject, name }, [attribute]);

			// Remove the attribute from the modified set
			setModifiedAttribNames((prevSet) => {
				const updatedSet = new Set(prevSet);
				updatedSet.delete(attribute);
				return updatedSet;
			});
		}
	};

	return { object, setObject, handleChange, handleBlur, setModifiedAttribNames, modifiedAttribNames };
};

export const useSubscriptionCheck = (UserSubscriptionPlanName) => {
	const history = useHistory();
	const { pathname, search } = useLocation();

	useEffect(() => {
		const urlParams = new URLSearchParams(search);
		const pdfToken = urlParams.get("token");

		if (!UserSubscriptionPlanName && !pdfToken) {
			const targetPage = encode(pathname + search);
			history.push(`/login?tgtUrl=${targetPage}`);
		}
	}, [UserSubscriptionPlanName, pathname, search, history]);
};

export const useModalHook = () => {
	const [isOpen, setOpen] = useState(false);
	const [modalProps, setModalProps] = useState(null);

	const openModal = useCallback(
		(arg) => {
			if (isOpen) return;
			setOpen(true);
			if (arg) setModalProps(arg);
		},
		[isOpen]
	);

	const closeModal = useCallback(() => {
		setOpen(false);
		setModalProps(null);
	}, []);
	return [openModal, closeModal, isOpen, modalProps];
};
export const useGetUserTeams = (user) => {
	const allTeamsObj = useSelector((state) => state.settings?.teams);
	const allAuthorizedUsers = useSelector((state) => state.settings?.authorizedUsers);

	const allTeams = allTeamsObj ? Object.values(allTeamsObj) : [];
	const authorizedUserId = allAuthorizedUsers.find((authUser) => authUser?.domainName.toLowerCase() === user?.loginName?.toLowerCase())?.id;
	const userTeams = allTeams?.filter((t) => t?.memberUserIds?.includes(authorizedUserId));

	return userTeams;
};

export const useGetUserWorkspaces = (loginName) => {
	const allActivesWorkspaces = useSelector(getAllActiveWorkspaces);
	const allAuthorizedUsers = useSelector((state) => state.settings?.authorizedUsers);

	const authorizedUserId = allAuthorizedUsers.find((authUser) => authUser?.domainName.toLowerCase() === loginName)?.id;
	const currentUserWorkspaces = allActivesWorkspaces?.filter((ws) => ws.authorizedUsers.find((au) => au.id === authorizedUserId));

	return currentUserWorkspaces;
};

export const useGetCurrentAuthorizedUser = () => {
	const currentUserId = useSelector((state) => state.auth.userInfo.UserId);
	const authorizedUsers = useSelector((state) => state.settings.authorizedUsers);
	return authorizedUsers?.filter((user) => user?.id === currentUserId);
};

export const useGetCurrentRegisteredUser = () => {
	const currentUserId = useSelector((state) => state.auth.userInfo.UserId);
	const authorizedUsers = useSelector((state) => state.settings.authorizedUsers);
	const registeredUsers = useSelector((state) => state.settings.user);

	const authUser = authorizedUsers?.filter((user) => user?.id === currentUserId);
	return registeredUsers?.filter((ru) => ru?.loginName?.toLowerCase() === authUser?.domainName.toLowerCase());
};

export const useGetIsCompanyAdmin = () => {
	const userInfo = useSelector((state) => state?.auth?.userInfo);
	const userType = Number(userInfo?.UserType);
	return userType === UserType.companyAdmin;
};

export const useGetIsLoggedUserWorkspaceOwner = (workspace) => {
	const userId = useSelector((state) => state?.auth?.userInfo?.UserId);
	return workspace?.authorizedUsers.find((u) => u?.id === userId)?.roleName === "owner";
};

export const useActionOnScroll = ({ pathname, authorizedPathnames, targetId, action }) => {
	useEffect(() => {
		if (authorizedPathnames?.includes(pathname)) {
			const target = $(targetId);
			target.on("scroll", action);

			return () => {
				target.off("scroll", action);
			};
		}
	}, []);
};

export function useAxiosInterceptor() {
	// Local state to store our dynamic header values

	const token = useSelector((state) => state.auth.token);
	const [connectionId, setConnectionId] = useState(null);
	// We store the interceptor ID in a ref so we can eject it when needed
	const interceptorIdRef = useRef(null);

	// This effect re-registers the interceptor whenever workspaceId, token,
	// or connectionId changes.
	useEffect(() => {
		// If we already have an interceptor, remove it first
		if (interceptorIdRef.current !== null) {
			axios.interceptors.request.eject(interceptorIdRef.current);
		}

		// Register a new interceptor with the latest values
		interceptorIdRef.current = axios.interceptors.request.use((config) => {
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
			if (currentWorkspaceId) {
				config.headers.WorkspaceId = currentWorkspaceId;
			}
			if (connectionId) {
				config.headers.ConnectionKey = connectionId;
			}
			return config;
		});

		// Cleanup function that ejects the interceptor if the component unmounts
		// or the effect re-runs
		return () => {
			axios.interceptors.request.eject(interceptorIdRef.current);
			interceptorIdRef.current = null;
		};
	}, [currentWorkspaceId, token, connectionId]);

	const updateConnectionID = useCallback((newConnectionId) => {
		setConnectionId(newConnectionId);
	}, []);

	// Return the updater functions and the current state if needed
	return {
		updateConnectionID,
	};
}
export const useInsertRecentWork = ({ entityName, recordId }) => {
	useEffect(() => {
		if (entityName) insertRecentWork({ entityName, recordId });
	}, []);
};
